import { ACCOUNT_SECURITY_CAPABILITIES } from '../../settings/capabilities.js';
import { PLATFORM_DASHBOARD_READ, PLATFORM_DATA_HUB_READ, PLATFORM_REPORTS_READ, } from '../../operations/dashboard.operations.js';
import { MARKET_PRICES_GET, MARKET_PRICES_LIST, } from '../../operations/market-data.operations.js';
import { ROUTE_IDS, OPERATION_IDS, I18N_KEYS } from './ids.js';
import { canonicalRouteRegistry as baseRouteRegistry } from './routes.js';
import { MARKET_PRICE_MANAGEMENT_ROUTES } from './market-price-routes.js';
const MAINTENANCE_DELEGATION_ROUTE_IDS = new Set([
    ROUTE_IDS.PLATFORM_SUPPORT_DELEGATIONS_LIST,
    ROUTE_IDS.PLATFORM_SUPPORT_DELEGATIONS_CREATE,
    ROUTE_IDS.PLATFORM_SUPPORT_DELEGATIONS_DETAIL,
    ROUTE_IDS.PLATFORM_SUPPORT_DELEGATIONS_REVOKE,
]);
const LIVE_PLATFORM_ADMIN_ROUTE_IDS = new Set([
    ROUTE_IDS.PLATFORM_USERS_LIST,
    ROUTE_IDS.PLATFORM_USERS_CREATE,
    ROUTE_IDS.PLATFORM_USERS_DETAIL,
    ROUTE_IDS.PLATFORM_USERS_EDIT,
    ROUTE_IDS.PLATFORM_MODULE_ASSIGNMENTS,
    ROUTE_IDS.PLATFORM_AUDIT_LIST,
    ROUTE_IDS.PLATFORM_AUDIT_DETAIL,
    ROUTE_IDS.PLATFORM_ACCESS_INSPECTOR,
]);
const DEMOTED_ROUTE_IDS = new Set([
    ROUTE_IDS.DATA_CLIENTS,
    ROUTE_IDS.DATA_CLIENT_DETAIL,
    ROUTE_IDS.DATA_PORTFOLIOS,
    ROUTE_IDS.DATA_PORTFOLIO_DETAIL,
    ROUTE_IDS.DATA_FX_RATES,
    ROUTE_IDS.DATA_BENCHMARKS,
    ROUTE_IDS.DATA_MARKET_DATA_PROVIDERS,
    ROUTE_IDS.DATA_MARKET_DATA_EXCEPTIONS,
    ROUTE_IDS.DATA_MANDATES,
    ROUTE_IDS.DATA_CUSTODY_BALANCES,
    ROUTE_IDS.DATA_MANAGEMENT_FEES,
    ROUTE_IDS.DATA_PERFORMANCE_FEES,
    ROUTE_IDS.DATA_JOURNALS,
    ROUTE_IDS.DATA_JOURNAL_DETAIL,
    ROUTE_IDS.DATA_CHART_OF_ACCOUNTS,
    ROUTE_IDS.DATA_FISCAL_PERIODS,
    ROUTE_IDS.DATA_COMPLIANCE_BREACHES,
    ROUTE_IDS.DATA_RISK_EXPOSURES,
    ROUTE_IDS.DATA_TAX_RECORDS,
    ROUTE_IDS.DATA_TAX_CALCULATIONS,
    ROUTE_IDS.DATA_EVIDENCE,
    ROUTE_IDS.DATA_EVIDENCE_DETAIL,
    ROUTE_IDS.ORGANIZATION_APPROVAL_POLICIES_LIST,
    ROUTE_IDS.ORGANIZATION_APPROVAL_POLICIES_DETAIL,
    ROUTE_IDS.ORGANIZATION_PERMISSION_BUNDLES_LIST,
    ROUTE_IDS.ORGANIZATION_PERMISSION_BUNDLES_DETAIL,
    ROUTE_IDS.ORGANIZATION_RETENTION_POLICIES,
    ROUTE_IDS.ORGANIZATION_EXPORT_POLICIES,
    ROUTE_IDS.ORGANIZATION_SECURITY_SETTINGS,
    ROUTE_IDS.ORGANIZATION_REGULATORY_PROFILE,
    ROUTE_IDS.ORGANIZATION_JURISDICTION_SETTINGS,
    ROUTE_IDS.ORGANIZATION_SUPPORT_ACCESS,
    ROUTE_IDS.PLATFORM_SECURITY_POSTURE,
    ROUTE_IDS.PLATFORM_DATA_PROVIDER_HEALTH,
    ROUTE_IDS.PLATFORM_MAINTENANCE_DELEGATIONS_LIST,
    ROUTE_IDS.PLATFORM_MAINTENANCE_DELEGATIONS_CREATE,
    ROUTE_IDS.PLATFORM_MAINTENANCE_DELEGATIONS_DETAIL,
]);
const projectedBaseRoutes = baseRouteRegistry.map((route) => {
    if (route.routeId === ROUTE_IDS.DATA_SETTLEMENT_INSTRUCTIONS ||
        route.routeId === ROUTE_IDS.DATA_SETTLEMENT_FAILURES ||
        route.routeId === ROUTE_IDS.DATA_POSITION_TRANSFERS) {
        return { ...route, availability: 'AVAILABLE', allowedPersonas: ['ORG_ADMIN'] };
    }
    if (route.routeId === ROUTE_IDS.SETTINGS_MFA) {
        return ACCOUNT_SECURITY_CAPABILITIES.mfaManagement
            ? {
                ...route,
                availability: 'AVAILABLE',
                navigationPlacement: 'avatar-menu',
            }
            : {
                ...route,
                availability: 'HIDDEN',
                navigationPlacement: 'hidden',
            };
    }
    if (route.routeId === ROUTE_IDS.DASHBOARD) {
        return {
            ...route,
            operationVariants: [
                { personas: ['ORG_CLIENT'], operationId: OPERATION_IDS.CLIENT_DASHBOARD_READ },
                {
                    personas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN', 'PLATFORM_USER'],
                    operationId: PLATFORM_DASHBOARD_READ,
                },
            ],
        };
    }
    if (route.routeId === ROUTE_IDS.DATA_HUB) {
        return {
            ...route,
            allowedPersonas: [
                'ORG_ADMIN',
                'ORG_USER',
                'SUPPORT_DELEGATE',
            ],
            operationVariants: [
                {
                    personas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN', 'PLATFORM_USER'],
                    operationId: PLATFORM_DATA_HUB_READ,
                },
            ],
        };
    }
    if (route.routeId === ROUTE_IDS.REPORTS) {
        const { disabledReasonKey: _disabledReasonKey, ...liveRoute } = route;
        return {
            ...liveRoute,
            availability: 'AVAILABLE',
            allowedPersonas: [
                'ORG_ADMIN',
                'ORG_USER',
                'ORG_CLIENT',
                'SUPPORT_DELEGATE',
            ],
            operationId: OPERATION_IDS.REPORTS_VIEW,
            operationVariants: [
                {
                    personas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN', 'PLATFORM_USER'],
                    operationId: PLATFORM_REPORTS_READ,
                },
                {
                    personas: ['ORG_CLIENT'],
                    operationId: OPERATION_IDS.CLIENT_REPORTS_READ_OWN,
                },
            ],
        };
    }
    if (route.routeId === ROUTE_IDS.DATA_MARKET_PRICES ||
        route.routeId === ROUTE_IDS.DATA_MARKET_PRICE_DETAIL) {
        const { disabledReasonKey: _disabledReasonKey, ...liveRoute } = route;
        return {
            ...liveRoute,
            availability: 'AVAILABLE',
            authentication: 'authenticated',
            allowedPersonas: ['ORG_ADMIN', 'SUPPORT_DELEGATE'],
            operationId: (route.routeId === ROUTE_IDS.DATA_MARKET_PRICES
                ? MARKET_PRICES_LIST
                : MARKET_PRICES_GET),
            auditPolicy: 'NONE',
        };
    }
    if (route.routeId === ROUTE_IDS.ORGANIZATION_JURISDICTION_SETTINGS) {
        const { disabledReasonKey: _disabledReasonKey, ...liveRoute } = route;
        return {
            ...liveRoute,
            availability: 'AVAILABLE',
            authentication: 'authenticated',
            allowedPersonas: ['ORG_ADMIN'],
            operationId: MARKET_PRICES_LIST,
            auditPolicy: 'ALWAYS',
        };
    }
    if (route.routeId === ROUTE_IDS.PLATFORM_MODULES_DETAIL) {
        const { disabledReasonKey: _disabledReasonKey, ...liveRoute } = route;
        return {
            ...liveRoute,
            path: '/platform/modules/:id',
            availability: 'AVAILABLE',
            authentication: 'authenticated-mfa',
            allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
            operationId: OPERATION_IDS.PLATFORM_MODULES_ASSIGN,
            auditPolicy: 'ALWAYS',
        };
    }
    if (MAINTENANCE_DELEGATION_ROUTE_IDS.has(route.routeId)) {
        const { disabledReasonKey: _disabledReasonKey, ...liveRoute } = route;
        return {
            ...liveRoute,
            availability: 'AVAILABLE',
            authentication: 'authenticated-mfa',
            allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
            auditPolicy: 'ALWAYS',
        };
    }
    if (LIVE_PLATFORM_ADMIN_ROUTE_IDS.has(route.routeId)) {
        const { disabledReasonKey: _disabledReasonKey, ...liveRoute } = route;
        return {
            ...liveRoute,
            availability: 'AVAILABLE',
            allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        };
    }
    if (DEMOTED_ROUTE_IDS.has(route.routeId)) {
        return {
            ...route,
            availability: 'PLANNED',
            navigationPlacement: 'hidden',
            disabledReasonKey: I18N_KEYS.COMMON_PLANNED,
        };
    }
    return route;
});
export const canonicalRouteRegistry = [
    ...projectedBaseRoutes,
    ...MARKET_PRICE_MANAGEMENT_ROUTES,
];
export function getRouteById(routeId) {
    return canonicalRouteRegistry.find((route) => route.routeId === routeId);
}
export function getRouteOperationIdForPersona(routeId, persona) {
    const route = getRouteById(routeId);
    return (route?.operationVariants?.find((variant) => variant.personas.includes(persona))?.operationId ??
        route?.operationId);
}
export function getRoutesByLayout(layoutId) {
    return canonicalRouteRegistry.filter((route) => route.layoutId === layoutId);
}
export function getRouteByPath(path) {
    const exact = canonicalRouteRegistry.find((route) => route.path === path);
    if (exact)
        return exact;
    for (const route of canonicalRouteRegistry) {
        if (!route.path.includes('/:'))
            continue;
        const pattern = route.path.replace(/:[^/]+/g, '[^/]+');
        if (new RegExp(`^${pattern}$`).test(path))
            return route;
    }
    return undefined;
}
export function getNavigationRoutes() {
    return canonicalRouteRegistry.filter((route) => route.navigationPlacement !== 'hidden' &&
        route.authentication !== 'public' &&
        (route.availability === 'AVAILABLE' || route.availability === 'READ_ONLY'));
}
export function isPublicRoute(routeId) {
    return getRouteById(routeId)?.authentication === 'public';
}
export function getRouteIds() {
    return canonicalRouteRegistry.map((route) => route.routeId);
}
